<template>
  <div class="artists-section">
    <div>
      <SessionGroupTabs
        v-if="!showDetails && sessionsList.length"
        :value="activeTab"
        :sessions="sessionsList"
        @change="changeCity"
      />
    </div>
    <div v-if="loading" class="loading">
      <i class="fas fa-spinner fa-spin"></i>
      加载艺人数据中...
    </div>
    <div v-else-if="error" class="error">
      <i class="fas fa-exclamation-circle"></i>
      {{ error }}
    </div>
    <div v-else>
      <div v-if="!showDetails" class="timetable">
        <!-- 日期标签页 -->
        <div class="date-tabs">
          <div v-for="dateGroup in artistsByDate" 
               :key="dateGroup.date" 
               class="date-tab"
               :class="{ active: activeDate === dateGroup.date }"
               @click="activeDate = dateGroup.date">
            <i class="fas fa-calendar-day"></i>
            {{ formatDate(dateGroup.date) }}
          </div>
        </div>
        <h3>
          <i class="fas fa-calendar-alt"></i>
          概览
        </h3>
        <div style="text-align: right; margin-bottom: 10px;">
          <button @click="toggleDetails" class="detail-toggle-btn">
            {{ showDetails ? '收起详情' : '展开详情' }}
          </button>
        </div>
        <!-- 日期内容 -->
        <div class="date-content">
          <div
            v-for="dateGroup in artistsByDate"
            :key="dateGroup.date"
            class="date-panel"
            :class="{ active: activeDate === dateGroup.date }"
          >
            <StagePeakBarChart
              :dataByDate="artistsByDate"
              :activeDate="dateGroup.date"
              :height="'500px'"
            />
            <StagePeakLineChart
              v-if="showLineChart"
              :activeDate="dateGroup.date"
              :height="'500px'"
              @artistClick="handleArtistChartClick"
            />
          </div>
        </div>
      </div>
      <div v-if="showDetails">
        <button @click="toggleDetails" class="detail-toggle-btn" style="margin-bottom: 10px;">
          返回概览
        </button>
        <transition name="stage-sections">
          <div class="stage-sections">
            <div
              v-for="dateGroup in artistsByDate"
              :key="dateGroup.date"
              v-show="activeDate === dateGroup.date"
            >
              <!-- 动态渲染所有舞台 -->
              <div
                v-for="stage in getStagesForDate(dateGroup)"
                :key="stage"
                class="stage-section"
              >
                <div class="stage-title" :style="{ borderBottomColor: stageTagStyle(stage).background }">
                  <i class="fas fa-music"></i>
                  {{ stage }}舞台
                </div>
                <div class="artists-grid">
                  <div
                    v-for="artist in dateGroup.artists.filter(a => a.stage === stage)"
                    :key="artist.name"
                    class="artist-card"
                    :class="{ active: selectedArtist === artist.name }"
                    @click="selectArtist(artist.name, dateGroup, artist)"
                  >
                    <div class="artist-content">
                      <div class="artist-header">
                        <div class="artist-name">
                          <i class="fas fa-user-music"></i>
                          {{ artist.name }}
                          <span class="stage-tag" :style="stageTagStyle(artist.stage)">
                            {{ artist.stage }}舞台
                          </span>
                        </div>
                        <div class="time-slot">
                          <i class="far fa-clock"></i>
                          {{ artist.startTime }} - {{ artist.endTime }}
                          <span class="duration">
                            <i class="fas fa-hourglass-half"></i>
                            {{ calculateDuration(artist.startTime, artist.endTime) }}
                          </span>
                        </div>
                      </div>
                      <div class="artist-chart">
                        <AudienceChart
                          :artist="artist.name"
                          :date="dateGroup.date"
                          :stage="artist.stage"
                          height="120px"
                          @dataLoaded="handleArtistDataLoaded"
                        />
                      </div>
                      <div class="artist-footer">
                        <div class="audience-info">
                          <div class="audience-stats">
                            <span>开始: {{ formatCount(artist.startCount) }}</span>
                            <span class="separator">·</span>
                            <span>峰值: {{ formatCount(artist.maxCount) }}</span>
                            <span class="separator">·</span>
                            <span>结束: {{ formatCount(artist.endCount) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- 添加艺人统计弹窗 -->
    <ArtistStatsModal
      v-if="showModal && selectedArtistData"
      :visible="showModal"
      :artist="selectedArtistData.artist"
      :date="selectedArtistData.date"
      :stage="selectedArtistData.stage"
      :start-time="selectedArtistData.startTime"
      :end-time="selectedArtistData.endTime"
      :duration="selectedArtistData.duration"
      :peak-count="selectedArtistData.peakCount"
      :avg-count="selectedArtistData.avgCount"
      @close="closeModal"
    />
  </div>
</template>

<script>
import AudienceChart from './AudienceChart.vue';
import ArtistStatsModal from './ArtistStatsModal.vue';
import SessionGroupTabs from './SessionGroupTabs.vue';
import StagePeakBarChart from './StagePeakBarChart.vue';
import StagePeakLineChart from './StagePeakLineChart.vue';
import { toRaw } from 'vue';
import api from '../api/config';
import { getStageColor, hexToRgba, resetStageColors } from '../utils/stageColors';
import { resolveDefaultSessionId } from '../utils/sessions';

export default {
  name: 'ArtistsList',
  components: {
    AudienceChart,
    ArtistStatsModal,
    SessionGroupTabs,
    StagePeakBarChart,
    StagePeakLineChart
  },
  props: {
    selectedArtist: {
      type: String,
      default: 'all'
    }
  },
  data() {
    return {
      artistsByDate: [],
      loading: false,
      error: null,
      maxAudienceCount: 0,
      activeDate: null,
      selectedArtistData: null,
      showModal: false,
      activeTab: null,
      sessionsList: [],
      stageData: [],
      showLineChart: false,
      toggleArtistsInfo: false,
      showDetails: false,
    };
  },
  async created() {
    resetStageColors();
    await this.fetchStageData();
    this.fetchArtists();
  },
  watch: {
    // artistsByDate: {
    //   immediate: true,
    //   handler(newDates) {
    //     if (newDates && newDates.length > 0) {
    //       this.activeDate = newDates[0].date;
    //     }
    //   }
    // },
    // activeTab: {
    //   immediate: true, // 可选，首次加载时也触发
    //   handler() {
    //     this.fetchArtists();
    //   }
    // }
    activeDate(newDate) {
      console.log(newDate, 'newnew')
      if (newDate) {
        this.fetchArtistData();
      }
    }
  },
  methods: {
    toggleDetails() {
      this.showDetails = !this.showDetails;
    },
    // 获取当前 dateGroup 中所有唯一舞台（排序后稳定）
    getStagesForDate(dateGroup) {
      if (!dateGroup || !dateGroup.artists) return [];
      return [...new Set(dateGroup.artists.map(a => a.stage).filter(Boolean))].sort();
    },
    // 根据舞台名返回标签的 inline style（background + color）
    stageTagStyle(stageName) {
      const bg = getStageColor(stageName);
      // 把颜色转成淡背景 + 深字体
      const bgLight = hexToRgba(bg, 0.18);
      return {
        background: bgLight,
        color: bg,
        border: `1px solid ${hexToRgba(bg, 0.4)}`,
      };
    },
    async fetchArtistData() {
      this.stageData = [];
      this.showLineChart = false;
      try {
        const response = await api.get(`/data/stage?date=${this.activeDate}`);
        const rawData = toRaw(response.data);
        this.stageData = rawData.data || [];
        this.showLineChart = true;
        console.log('舞台数据获取完成：', this.stageData.length, this.showLineChart);
        // 将stageData存储到sessionStorage
        sessionStorage.setItem('stageData', JSON.stringify(this.stageData));
      } catch (error) {
        console.error('获取舞台数据失败：', error);
        this.error = '加载舞台数据失败: ' + (error.message || '未知错误');
      }
    },
    async fetchStageData() {
      // this.stageData = [];
      // this.showLineChart = false;
      try {
        const response = await api.get(`/sessions`);
        this.sessionsList = response.data.sessions || [];
        const storedSessionId = localStorage.getItem('sessionId');
        this.activeTab = resolveDefaultSessionId(this.sessionsList, storedSessionId);
        if (this.activeTab) {
          localStorage.setItem('sessionId', this.activeTab);
        }
        console.log('场次数据获取完成：', this.sessionsList.length);
      } catch (error) {
        console.error('获取场次数据失败：', error);
        this.error = '加载场次数据失败: ' + (error.message || '未知错误');
      }
    },
    handleArtistChartClick({ artist, date }) {
      const dateGroup = this.artistsByDate.find(d => d.date === date);
      console.log(dateGroup, 'art dateGroup')
      if (!dateGroup) return;

      const artistData = dateGroup.artists.find(a => a.name === artist);
      console.log(artistData, 'art artistData')
      if (!artistData) return;

      this.selectArtist(artist, dateGroup, artistData);
    },
    
    changeCity(city) {
      this.activeTab = city
      localStorage.setItem('sessionId', this.activeTab)
      resetStageColors();
      this.fetchArtists()
    },
    formatDate(dateStr) {
      const date = new Date(dateStr);
      return `${date.getMonth() + 1}月${date.getDate()}日`;
    },
    calculateDuration(startTime, endTime) {
      if (!startTime || !endTime) return '';
      
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);
      
      let durationMinutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
      
      // 如果跨越午夜
      if (durationMinutes < 0) {
        durationMinutes += 24 * 60;
      }
      
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      
      if (hours === 0) {
        return `${minutes}分钟`;
      } else if (minutes === 0) {
        return `${hours}小时`;
      } else {
        return `${hours}小时${minutes}分钟`;
      }
    },
    formatCount(count) {
      if (!count) return '0';
      return count.toLocaleString();
    },
    getCountClass(count) {
      if (!count) return 'count-low';
      const ratio = count / this.maxAudienceCount;
      if (ratio >= 0.8) return 'count-high';
      if (ratio >= 0.5) return 'count-medium';
      return 'count-low';
    },
    getCountIcon(count) {
      if (!count) return 'fas fa-user-alt';
      const ratio = count / this.maxAudienceCount;
      if (ratio >= 0.8) return 'fas fa-users fa-lg';
      if (ratio >= 0.5) return 'fas fa-user-friends';
      return 'fas fa-user-alt';
    },
    getCountStyle(count) {
      if (!count || !this.maxAudienceCount) return {};
      const ratio = count / this.maxAudienceCount;
      return {
        width: `${Math.max(60, ratio * 120)}px`
      };
    },
    async fetchArtists() {
      try {
        this.loading = true;
        this.error = null;
        
        const response = await api.get('/artists/by-date');
        console.log('API Response:', response.data);

        if (!response.data || !response.data.artistsByDate) {
          throw new Error('Invalid response format');
        }

        // 按日期排序
        this.artistsByDate = response.data.artistsByDate.sort((a, b) =>
          new Date(a.date) - new Date(b.date)
        );

        // 设置默认日期为新的数据的第一个日期
        if (this.artistsByDate.length > 0) {
          this.activeDate = this.artistsByDate[0].date;
        } else {
          this.activeDate = null;
        }

        // 计算最大观众数
        this.maxAudienceCount = Math.max(
          ...this.artistsByDate.flatMap(group =>
            group.artists.map(artist => artist.maxCount || 0)
          )
        );
        this.loading = false;
      } catch (error) {
        console.error('Error fetching artists:', error);
        this.error = '加载艺人列表失败: ' + (error.message || '未知错误');
        this.loading = false;
      }
    },
    selectArtist(artist, dateGroup, artistData) {
      this.$emit('update:selectedArtist', artist);
      
      // 计算平均观众数
      const avgCount = Math.round(
        (artistData.startCount + artistData.maxCount + artistData.endCount) / 3
      );
      
      this.selectedArtistData = {
        artist: artist,
        date: dateGroup.date,
        stage: artistData.stage,
        startTime: artistData.startTime,
        endTime: artistData.endTime,
        duration: this.calculateDuration(artistData.startTime, artistData.endTime),
        peakCount: artistData.maxCount,
        avgCount: avgCount,
      };
      
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.selectedArtistData = null;
    },
  }
};
</script>

<style scoped>
.artists-section {
  margin: 20px auto;
  padding: 20px;
  background: linear-gradient(to bottom right, #ffffff, #f8f9fa);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-width: 1200px;
}

.artists-section h3 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 1.2em;
  display: flex;
  align-items: center;
  gap: 8px;
}

.artists-section h3 i {
  color: #1890ff;
}

.timetable {
  margin: 0 auto;
}

.date-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 5px;
  scrollbar-width: thin;
  scrollbar-color: #1890ff #f0f0f0;
}

.date-tabs::-webkit-scrollbar {
  height: 6px;
}

.date-tabs::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}

.date-tabs::-webkit-scrollbar-thumb {
  background: #1890ff;
  border-radius: 3px;
}

.date-tab {
  padding: 10px 20px;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #666;
}

.date-tab:hover {
  background: #e6f7ff;
  border-color: #91d5ff;
  color: #1890ff;
}

.date-tab.active {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  border-color: #1890ff;
  color: white;
}

.date-content {
  position: relative;
}

.date-panel {
  display: none;
  animation: fadeIn 0.3s ease;
}

.date-panel.active {
  display: block;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stage-sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.stage-section {
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.stage-title {
  font-size: 1.1em;
  font-weight: 500;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.stage-title i {
  color: #1890ff;
}

.artists-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
}

.artist-card {
  background: white;
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.artist-card:hover {
  transform: translateY(-2px);
  background: #e6f7ff;
  border-color: #91d5ff;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.1);
}

.artist-card.active {
  background: #e6f7ff;
  border-color: #1890ff;
}

.artist-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.artist-name {
  font-size: 16px;
  color: #333;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.artist-name i {
  color: #722ed1;
  font-size: 0.9em;
}

.time-slot {
  font-family: monospace;
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.time-slot i {
  color: #faad14;
  font-size: 0.9em;
}

.time-slot .duration {
  color: #8c8c8c;
  font-size: 12px;
  background: #f5f5f5;
  padding: 2px 8px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
}

.time-slot .duration i {
  font-size: 10px;
  color: #b37feb;
}

.artist-footer {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
}

.audience-info {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
}

.audience-stats {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #8c8c8c;
}

.separator {
  color: #d9d9d9;
}

.loading, .error {
  text-align: center;
  padding: 20px;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.error {
  color: #f5222d;
}

.stage-tag {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 8px;
  /* 颜色通过 stageTagStyle() 动态注入 */
}

.artist-chart {
  margin: 10px -15px;
  padding: 0 15px;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
}

@media (max-width: 768px) {
  .date-tabs {
    padding-bottom: 10px;
  }
  
  .date-tab {
    padding: 8px 15px;
    font-size: 0.9em;
  }
  
  .artists-grid {
    grid-template-columns: 1fr;
  }
  
  .artist-card {
    padding: 12px;
  }
}
.detail-toggle-btn {
  background: #1890ff;
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}

.detail-toggle-btn:hover {
  background: #40a9ff;
}

/* 详情模块切换动画 */
.stage-sections {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.stage-sections-enter-active,
.stage-sections-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.stage-sections-enter,
.stage-sections-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>